import {useCallback, useEffect, useRef, useState} from "react";
import screenfull from "screenfull";
//@ts-ignore
import "./App.css";
import MainScreen from "./pages/MainScreen";
import SplashScreen from "./pages/SplashScreen";
import {Routes, Route} from "react-router-dom";
import SettingsModal from "./components/SettingsModal";
import { FullScreenContext } from "./contexts/FullScreenContext";
import { useSettingsContext } from "./contexts/SettingsContext";
import { ExerciserSDK, IExerciserSDK } from "ap-exerciser-sdk";
import { initializeApSharedLib } from "./utils/initializeAPSL";
import Profile from "./pages/Profile";
import { IntlProvider } from "react-intl";

const LANGUAGES = ["en", "fr"];

const appConfig = {
  appName: process.env.REACT_APP_NAME,
  skinsAppName: process.env.REACT_APP_SKINS_NAME,
  language: process.env.REACT_APP_LANGUAGE,
  contentManager: {
    projectName: process.env.REACT_APP_CONTENT_MANAGER_PROJECT_NAME,
    identity: {
      client_id: process.env.REACT_APP_CONTENT_MANAGER_IDENTITY_CLIENT_ID,
      client_secret: process.env.REACT_APP_CONTENT_MANAGER_IDENTITY_CLIENT_SECRET,
      scope: process.env.REACT_APP_CONTENT_MANAGER_IDENTITY_SCOPE,
      grant_type: process.env.REACT_APP_CONTENT_MANAGER_IDENTITY_GRANT_TYPE,
    },
    baseUrl: process.env.REACT_APP_CONTENT_MANAGER_BASE_URL,
  },
  firebase: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  },
  PUQ: {
    geoFunctionUrl: process.env.REACT_APP_FIREBASE_GEO_FUNCTION,
  },
  sessionManager: {
    storageOption: process.env.REACT_APP_SESSION_MANAGER_STORAGE_OPTION,
    initStats: {
      sessionStats:{
        sessionExerciseCount: 0,
        sessionWordList: []
      }
    }
  },
  apiConfig: {
    baseUrl: process.env.REACT_APP_API_CONFIG_BASE_URL,
  },
  analyticsPrefix: process.env.REACT_APP_ANALYTICS_PREFIX,
};

function App() {
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sdk, setSDK] = useState<any>();
  const [splash, setSplash] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [transData, setTransData] = useState<any>({});
  const {onSetSettings, setSkin} = useSettingsContext();
  const language = useRef<string>('fr');

  useEffect(() => {
    const languageParam = new URLSearchParams(document.location.search).get("locale")
    if (languageParam) language.current = languageParam
  }, []);

  const initializeSdk = async () => {
    if (!appConfig.sessionManager.storageOption || appConfig.sessionManager.storageOption.length===0) {
      appConfig.sessionManager.storageOption = "local"
    }
    const sdk: any = new ExerciserSDK(appConfig, (instance: any) => {
      setSDK(instance);
    });

    initializeApSharedLib(appConfig.language, sdk);
  };

  const exitHandler = (event: any) => {
    if (
      !document.fullscreen &&
      !(document as any).webkitIsFullScreen &&
      !(document as any).mozFullScreen &&
      !(document as any).msFullscreenElement
    ) {
      event.preventDefault();
      screenfull.exit();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    initializeSdk();

    document.addEventListener("webkitfullscreenchange", exitHandler, false);
    document.addEventListener("mozfullscreenchange", exitHandler, false);
    document.addEventListener("fullscreenchange", exitHandler, false);
    document.addEventListener("MSFullscreenChange", exitHandler, false);

    return () => {
      document.removeEventListener("webkitfullscreenchange", exitHandler);
      document.removeEventListener("mozfullscreenchange", exitHandler);
      document.removeEventListener("fullscreenchange", exitHandler);
      document.removeEventListener("MSFullscreenChange", exitHandler);
    };
  }, []);

  let state = {
    fullScreen: fullscreen,
    toggleFullScreen: () => {
      if (!fullscreen) {
        screenfull.request(document.body);
      } else {
        screenfull.exit();
      }
      setFullscreen(!fullscreen);
    },
  };

  const readContent = useCallback(async () => {
    if (sdk) {
      try {
        setLoading(true);
        const data = await sdk.getDataManager().getStaticTexts();
        const mediaFiles = await sdk.getDataManager().getMediaFiles();
        if (data.items) {
          const wordsData = data.items.map((item: any) => ({
            id: item.data.slug.iv,
            label: item.data.label,
          }));

          const filesData = mediaFiles.map((item: any) => ({
            id: 'file-' + item.data.name.iv,
            label: {
              en: item.data.assetEn,
              fr: item.data.assetFr
            },
          }));

          const stagingData = [...wordsData, ...filesData];

          const stagingConverted = Object.assign({});

          LANGUAGES.map((language) => {
            stagingConverted[language] = stagingData.reduce(
              (result: any, langItem: any) => {
                result[langItem.id] = langItem.label[language];

                return result;
              },
              {}
            );
          });

          setTransData(stagingConverted);
        }

        setLoading(false);
        setLoaded(true);
      } catch (error) {}
    }
  }, [sdk]);

  const loadSettings = useCallback(async () => {
    if (sdk) {
      try {
        const settings = await sdk.getDataManager().getSettings();
        onSetSettings(settings);
        if (settings.bg) {
          const skin = await sdk.getDataManager().getSkin(settings.bg);
          setSkin(skin);
        }
      } catch (error) {}
    }
  }, [onSetSettings, sdk, setSkin]);

  useEffect(() => {
    if (!loading && !loaded && sdk) {
      readContent();
      loadSettings();
    }
  }, [sdk, loading, loaded]);

  return (
    <FullScreenContext.Provider value={state}>
      <IntlProvider locale={language.current} messages={transData[language.current]}>
        <div className="App">
          <Routes>
            <Route
              path="/"
              element={
                <SplashScreen sdk={sdk} splash={splash} setSplash={setSplash} />
              }
            />
            <Route path="/game" element={<MainScreen sdk={sdk} />} />
            {/* <Route path="/wordlist" element={<NewScenario sdk={sdk} />} /> */}
          </Routes>
          <SettingsModal sdk={sdk} />
        </div>
      </IntlProvider>
    </FullScreenContext.Provider>
  );
}

export default App;
